// Settings (UI kit W5): the sections down the left, one at a time on the
// right, each with its own address (/settings/design). Business details
// save with a button; the invoice design saves as it changes, like the
// phone app's Customize.
import { type FormEvent, type ReactNode, useMemo, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { useAppData } from '@/app/AppData';
import { type ThemeChoice, useTheme } from '@/app/theme';
import { usePageTitle } from '@/app/usePageTitle';
import { deleteBusiness, mergeBusiness } from '@/app/actions';
import { type Business, businessInitials, canAddBusiness, draftInvoice, FREE_BUSINESS_LIMIT, formatInvoiceNumber } from '@/core/business';
import { isEmailAddress } from '@/core/client';
import { addDays, nowStamp, today } from '@/core/dates';
import { argbToHex, BRAND_COLOURS, FONTS, hexToArgb, TEMPLATES } from '@/core/design';
import { type InvoiceDesign, NO_TAX } from '@/core/invoice';
import { CURRENCIES, formatBasisPoints, Money, parseBasisPoints } from '@/core/money';
import type { SavedItem } from '@/core/savedItem';
import { isUsageSharingOn, setUsageSharing, track } from '@/data/analytics';
import { canDeleteAccountNow, deleteAccount, signOut } from '@/data/authService';
import { deleteItem, saveBusiness, saveItem } from '@/data/store';
import { ScaledPaper } from '@/invoice/InvoicePaper';
import { currencyLabel, templateLabel } from '@/l10n/labels';
import { t } from '@/l10n/strings';
import { NotFoundPage } from '@/pages/System';
import { Button, ButtonLink, PageHead, PageLoading, ProBadge, SelectField, Switch, TextArea, TextField } from '@/ui/controls';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Icon, type IconName } from '@/ui/Icon';
import { LogoPicker } from '@/ui/LogoPicker';
import { SignaturePad } from '@/ui/SignaturePad';
import { useToast } from '@/ui/Toasts';

type Section = 'profile' | 'businesses' | 'invoicing' | 'items' | 'payment' | 'design' | 'appearance' | 'account';

const SECTIONS: { id: Section; icon: IconName; label: () => string }[] = [
  { id: 'profile', icon: 'store', label: () => t('webTitleProfile') },
  { id: 'businesses', icon: 'grid', label: () => t('businessesTitle') },
  { id: 'invoicing', icon: 'file', label: () => t('settingsInvoicing') },
  { id: 'items', icon: 'package', label: () => t('itemsTitle') },
  { id: 'payment', icon: 'bank', label: () => t('settingsPaymentDetails') },
  { id: 'design', icon: 'palette', label: () => t('settingsInvoiceDesign') },
  { id: 'appearance', icon: 'moon', label: () => t('webAppearance') },
  { id: 'account', icon: 'user', label: () => t('webAccount') },
];

export function SettingsPage() {
  const { section = 'profile' } = useParams();
  const { business } = useAppData();
  const current = SECTIONS.find((s) => s.id === section);
  usePageTitle(current ? current.label() : t('navSettings'));
  if (!current) return <NotFoundPage />;
  if (!business) return <PageLoading />;
  return (
    <div className="page">
      <PageHead title={t('navSettings')} subtitle={t('webSettingsSubtitle')} />
      <div className="settings">
        <nav className="settings__nav" aria-label={t('navSettings')}>
          {SECTIONS.map((s) => (
            <NavLink key={s.id} to={s.id === 'profile' ? '/settings' : `/settings/${s.id}`} end className="settings__link" aria-current={s.id === current.id ? 'page' : undefined}>
              <Icon name={s.icon} size={18} />
              {s.label()}
            </NavLink>
          ))}
        </nav>
        <div className="settings__body">
          {current.id === 'profile' && <ProfileSection key={business.id} business={business} />}
          {current.id === 'businesses' && <BusinessesSection />}
          {current.id === 'invoicing' && <InvoicingSection key={business.id} business={business} />}
          {current.id === 'items' && <ItemsSection />}
          {current.id === 'payment' && <PaymentSection key={business.id} business={business} />}
          {current.id === 'design' && <DesignSection business={business} />}
          {current.id === 'appearance' && <AppearanceSection />}
          {current.id === 'account' && <AccountSection />}
        </div>
      </div>
    </div>
  );
}

/** A section's card: title, detail, the fields and a save row. */
function SectionCard({ title, detail, children, onSubmit, dirty, aside }: { title: string; detail?: string; children: ReactNode; onSubmit?: () => void; dirty?: boolean; aside?: ReactNode }) {
  const body = (
    <>
      <div className="between" style={{ alignItems: 'flex-start' }}>
        <div className="stack-1">
          <h2 className="title-serif" style={{ fontSize: 28, lineHeight: '32px' }}>
            {title}
          </h2>
          {detail && <p className="caption">{detail}</p>}
        </div>
        {aside}
      </div>
      {children}
      {onSubmit && (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button type="submit" variant="action" disabled={!dirty}>
            {t('profileSaveChanges')}
          </Button>
        </div>
      )}
    </>
  );
  return onSubmit ? (
    <form
      className="card stack-6"
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {body}
    </form>
  ) : (
    <section className="card stack-6">{body}</section>
  );
}

function useSave() {
  const { user } = useAppData();
  const toast = useToast();
  return (business: Business, setting: string) => {
    if (!user) return;
    saveBusiness(user.uid, { ...business, updatedAt: nowStamp() });
    track('setting_changed', { setting });
    toast(t('profileBusinessDetailsSaved'));
  };
}

function ProfileSection({ business }: { business: Business }) {
  const save = useSave();
  const [form, setForm] = useState({ name: business.name, email: business.email, phone: business.phone, address: business.address, logo: business.logo });
  const [showErrors, setShowErrors] = useState(false);
  const dirty = form.name !== business.name || form.email !== business.email || form.phone !== business.phone || form.address !== business.address || form.logo !== business.logo;
  const nameError = !form.name.trim() ? t('errorBusinessNameMissing') : null;
  const emailError = form.email.trim() && !isEmailAddress(form.email) ? t('errorEmailInvalid') : null;
  return (
    <SectionCard
      title={t('webTitleProfile')}
      detail={t('profileTheseDetailsAppearOnEvery')}
      dirty={dirty}
      onSubmit={() => {
        setShowErrors(true);
        if (nameError || emailError) return;
        save({ ...business, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), address: form.address.trim(), logo: form.logo }, 'business_profile');
      }}
    >
      <LogoPicker logo={form.logo} name={form.name} onChange={(logo) => setForm((f) => ({ ...f, logo }))} />
      <div className="grid-2">
        <TextField label={t('profileBusinessName')} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} error={showErrors ? nameError : null} autoComplete="organization" />
        <TextField label={t('profileBusinessEmail')} type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} error={showErrors ? emailError : null} autoComplete="email" />
      </div>
      <div className="grid-2">
        <TextField label={t('profilePhone')} type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} autoComplete="tel" />
        <TextArea label={t('profileBusinessAddress')} value={form.address} rows={2} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} autoComplete="street-address" />
      </div>
    </SectionCard>
  );
}

const TERMS = [0, 7, 14, 30, 45, 60, 90];

function InvoicingSection({ business }: { business: Business }) {
  const save = useSave();
  const [currency, setCurrency] = useState(business.currency);
  const [terms, setTerms] = useState(business.paymentTermsDays);
  const [prefix, setPrefix] = useState(business.invoicePrefix);
  const [next, setNext] = useState(String(business.nextInvoiceNumber));
  const [notes, setNotes] = useState(business.defaultNotes);
  const [chargesTax, setChargesTax] = useState(business.tax.basisPoints > 0);
  const [taxName, setTaxName] = useState(business.tax.name || t('taxDefaultName'));
  const [taxRate, setTaxRate] = useState(business.tax.basisPoints ? formatBasisPoints(business.tax.basisPoints).replace('%', '') : '');
  const [showErrors, setShowErrors] = useState(false);
  const number = /^\d{1,7}$/.test(next.trim()) ? Number(next) : null;
  const numberError = !number || number < 1 ? t('errorInvoiceNumberInvalid') : null;
  const prefixError = prefix.length > 12 ? t('errorInvoicePrefixTooLong') : null;
  const rate = parseBasisPoints(taxRate);
  const taxError = chargesTax && (!rate || rate <= 0) ? t('errorTaxRateInvalid') : null;
  const tax = chargesTax && rate ? { name: taxName.trim() || t('taxDefaultName'), basisPoints: rate } : NO_TAX;
  const dirty =
    currency !== business.currency ||
    terms !== business.paymentTermsDays ||
    prefix !== business.invoicePrefix ||
    next !== String(business.nextInvoiceNumber) ||
    notes !== business.defaultNotes ||
    tax.name !== business.tax.name ||
    tax.basisPoints !== business.tax.basisPoints;
  const example = formatInvoiceNumber({ ...business, invoicePrefix: prefix }, number ?? business.nextInvoiceNumber);
  return (
    <SectionCard
      title={t('settingsInvoicing')}
      detail={t('webInvoicingDetail')}
      dirty={dirty}
      onSubmit={() => {
        setShowErrors(true);
        if (numberError || prefixError || taxError) return;
        save({ ...business, currency, paymentTermsDays: terms, invoicePrefix: prefix, nextInvoiceNumber: number!, defaultNotes: notes.trim(), tax }, 'invoicing');
      }}
    >
      <div className="grid-2">
        <SelectField label={t('commonDefaultCurrency')} value={currency} help={t('settingsUsedForNewInvoicesInvoicesAlready')} onChange={(e) => setCurrency(e.target.value)}>
          {CURRENCIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code} {c.symbol} · {currencyLabel(c.code)}
            </option>
          ))}
        </SelectField>
        <SelectField label={t('commonPaymentTerms')} value={terms} help={t('settingsHowLongClientsHaveToPay')} onChange={(e) => setTerms(Number(e.target.value))}>
          {[...new Set([...TERMS, business.paymentTermsDays])]
            .sort((a, b) => a - b)
            .map((d) => (
              <option key={d} value={d}>
                {t('settingsPaymentTerms', { days: d })}
              </option>
            ))}
        </SelectField>
      </div>
      <div className="grid-2">
        <TextField label={t('settingsPrefix')} value={prefix} onChange={(e) => setPrefix(e.target.value)} error={showErrors ? prefixError : null} help={t('settingsYourNextInvoiceWillBe', { example })} />
        <TextField
          label={t('settingsNextNumber')}
          inputMode="numeric"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          error={showErrors ? numberError : null}
          help={number && number < business.nextInvoiceNumber ? t('settingsGoingBackToALowerNumber') : undefined}
        />
      </div>
      <TextArea label={t('commonDefaultNotes')} value={notes} rows={3} placeholder={t('settingsEGThankYouForYour')} onChange={(e) => setNotes(e.target.value)} />
      <div>
        <Switch label={t('taxDefaultTitle')} detail={t('taxAfterDiscountNote')} checked={chargesTax} onChange={setChargesTax} />
        {chargesTax && (
          <div className="grid-2" style={{ marginTop: 8 }}>
            <TextField label={t('taxName')} value={taxName} onChange={(e) => setTaxName(e.target.value)} />
            <TextField label={t('taxRatePercentLabel')} inputMode="decimal" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} error={showErrors ? taxError : null} />
          </div>
        )}
      </div>
    </SectionCard>
  );
}

function PaymentSection({ business }: { business: Business }) {
  const save = useSave();
  const [bank, setBank] = useState(business.bank);
  const dirty = bank.bankName !== business.bank.bankName || bank.accountName !== business.bank.accountName || bank.accountNumber !== business.bank.accountNumber;
  return (
    <SectionCard
      title={t('settingsPaymentDetails')}
      detail={t('setupYourAddressAndBankDetails')}
      dirty={dirty}
      onSubmit={() => save({ ...business, bank: { bankName: bank.bankName.trim(), accountName: bank.accountName.trim(), accountNumber: bank.accountNumber.trim() } }, 'payment_details')}
    >
      <div className="grid-2">
        <TextField label={t('profileBankName')} value={bank.bankName} onChange={(e) => setBank((b) => ({ ...b, bankName: e.target.value }))} />
        <TextField label={t('profileAccountNumber')} inputMode="numeric" value={bank.accountNumber} onChange={(e) => setBank((b) => ({ ...b, accountNumber: e.target.value }))} />
      </div>
      <TextField label={t('profileAccountName')} value={bank.accountName} onChange={(e) => setBank((b) => ({ ...b, accountName: e.target.value }))} />
    </SectionCard>
  );
}

function DesignSection({ business }: { business: Business }) {
  const { user, proUnlocked, offersPro, isPro, config } = useAppData();
  const navigate = useNavigate();
  const [drawing, setDrawing] = useState(false);
  const design = business.design;
  const update = (patch: Partial<InvoiceDesign>, setting: string) => {
    if (!user) return;
    saveBusiness(user.uid, { ...business, design: { ...design, ...patch }, updatedAt: nowStamp() });
    track('design_default_changed', { setting });
  };
  const sample = useMemo(() => {
    const day = today();
    const draft = draftInvoice(business, { id: 'sample', today: day, now: nowStamp() });
    return {
      ...draft,
      client: { name: t('webSampleClient'), email: '', phone: '', address: '' },
      dueDate: addDays(day, business.paymentTermsDays),
      items: [
        { id: '1', name: t('webSampleItemOne'), description: t('webSampleItemOneDetail'), quantity: 1, rateMinor: 8000000 },
        { id: '2', name: t('webSampleItemTwo'), description: '', quantity: 4, rateMinor: 1000000 },
      ],
    };
  }, [business]);
  const custom = !BRAND_COLOURS.includes(design.brandColor >>> 0);
  return (
    <div className="design">
      <SectionCard title={t('settingsInvoiceDesign')} detail={t('customizeSetsTheLookOfNew')}>
        <fieldset className="stack-2">
          <legend className="field__label">{t('customizeTemplate')}</legend>
          <div className="templates">
            {TEMPLATES.filter((tpl) => !tpl.isPro || proUnlocked || offersPro).map((tpl) => {
              const locked = tpl.isPro && !proUnlocked;
              return (
                <button
                  key={tpl.id}
                  type="button"
                  className="template"
                  aria-pressed={design.templateId === tpl.id}
                  aria-label={locked ? t('templateSemanticLabelPro', { name: templateLabel(tpl.id) }) : t('templateSemanticLabel', { name: templateLabel(tpl.id) })}
                  onClick={() => (locked ? navigate('/pro') : (update({ templateId: tpl.id }, 'template'), track('template_chosen', { template: tpl.id, font: design.fontId, is_pro_template: tpl.isPro })))}
                >
                  <span className={`template__page template__page--${tpl.id}`} style={{ ['--tpl' as string]: argbToHex(design.brandColor) }} aria-hidden>
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="row" style={{ gap: 6 }}>
                    {templateLabel(tpl.id)}
                    {locked && <ProBadge />}
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>
        <fieldset className="stack-2">
          <legend className="field__label">{t('customizeBrandColour')}</legend>
          <div className="swatches">
            {BRAND_COLOURS.map((argb) => (
              <button key={argb} type="button" className="swatch" style={{ background: argbToHex(argb) }} aria-pressed={(design.brandColor >>> 0) === argb} aria-label={t('customizeColourLabel', { hex: argbToHex(argb) })} onClick={() => update({ brandColor: argb }, 'brand_colour')} />
            ))}
            <label className="swatch swatch--custom" aria-pressed={custom} title={t('customizeAnotherColour')} style={custom ? { background: argbToHex(design.brandColor) } : undefined}>
              <span className="vh">{t('customizeAnotherColour')}</span>
              <input
                type="color"
                value={argbToHex(design.brandColor).toLowerCase()}
                onChange={(e) => {
                  const argb = hexToArgb(e.target.value);
                  if (argb !== null) update({ brandColor: argb }, 'brand_colour');
                }}
              />
            </label>
          </div>
        </fieldset>
        <fieldset className="stack-2">
          <legend className="field__label">{t('customizeFont')}</legend>
          <div className="row-wrap">
            {FONTS.map((f) => (
              <button key={f.id} type="button" className="tab" aria-pressed={design.fontId === f.id} style={{ fontFamily: `'inv-${f.file}'`, fontWeight: 500 }} onClick={() => update({ fontId: f.id }, 'font')}>
                {f.label}
              </button>
            ))}
          </div>
        </fieldset>
        <div>
          <Switch label={t('customizeBusinessLogo')} detail={t('customizeShowOnInvoice')} checked={design.showLogo} onChange={(v) => update({ showLogo: v }, 'show_logo')} />
          <Switch
            label={t('customizeTaxVatLine')}
            detail={business.tax.basisPoints ? t('customizeNewInvoicesStartWith', { label: `${business.tax.name} (${formatBasisPoints(business.tax.basisPoints)})` }) : t('customizeSetATaxInSettings')}
            checked={design.showTax}
            disabled={!business.tax.basisPoints}
            onChange={(v) => update({ showTax: v }, 'show_tax')}
          />
          <Switch
            label={t('customizePaymentReference')}
            detail={t('customizePaymentReferenceDetail')}
            checked={design.showPaymentReference}
            onChange={(v) => update({ showPaymentReference: v }, 'show_payment_reference')}
          />
          <Switch
            label={t('customizeSignature')}
            detail={business.signature ? t('customizeYourSignature') : t('customizeDrawItOnceAndIt')}
            checked={design.showSignature && !!business.signature}
            onChange={(v) => (v && !business.signature ? setDrawing(true) : update({ showSignature: v }, 'show_signature'))}
          />
          {business.signature && (
            <div className="row" style={{ paddingBottom: 12 }}>
              <img src={business.signature} alt={t('customizeYourSignature')} className="signature-thumb" />
              <Button size="sm" onClick={() => setDrawing(true)}>
                {t('customizeRedraw')}
              </Button>
            </div>
          )}
          {/* Removing the line needs a real Pro plan, and isn't offered before Pro is on sale. */}
          {config.flags.proLaunched && isPro && <Switch label={t('customizeMadeWithPaidlarkLine')} checked={design.showPaidlarkLine} onChange={(v) => update({ showPaidlarkLine: v }, 'paidlark_line')} />}
        </div>
      </SectionCard>
      <aside className="design__preview" aria-label={t('previewPreview')}>
        <span className="overline">{t('editorLivePreviewA4')}</span>
        <ScaledPaper invoice={sample} business={business} isPro={isPro} />
      </aside>
      {drawing && (
        <SignaturePad
          onClose={() => setDrawing(false)}
          onSave={(signature) => {
            if (!user) return;
            saveBusiness(user.uid, { ...business, signature, design: { ...design, showSignature: true }, updatedAt: nowStamp() });
            track('design_default_changed', { setting: 'signature' });
            setDrawing(false);
          }}
        />
      )}
    </div>
  );
}

/** My items: the saved products and services, to rename, reprice or delete. */
const nameOf = (b: Business) => b.name.trim() || t('businessesUnnamed');

/** Every business: add one, merge one into another, or delete one (never the only one). */
function BusinessesSection() {
  const { user, business, businesses = [], every, setActiveBusiness, isPro, offersPro } = useAppData();
  const navigate = useNavigate();
  const toast = useToast();
  const [merging, setMerging] = useState<Business | null>(null);
  const [deleting, setDeleting] = useState<Business | null>(null);
  const canAdd = canAddBusiness(businesses.length, isPro);
  const canRemove = businesses.length > 1 && every !== undefined && user !== null;
  return (
    <SectionCard title={t('businessesTitle')} detail={businesses.length > 1 ? t('businessesCount', { count: businesses.length }) : t('businessesOnlyOne')}>
      <ul className="saved-items">
        {businesses.map((b) => (
          <li key={b.id} className="row" style={{ padding: 'var(--space-3) 0' }}>
            <span className="avatar avatar--grey" aria-hidden>
              {b.logo ? <img src={b.logo} alt="" /> : businessInitials(b.name)}
            </span>
            <span className="grow stack-1" style={{ minWidth: 0 }}>
              <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOf(b)}</strong>
              {b.id === business?.id && <span className="caption">{t('businessesInUse')}</span>}
            </span>
            {canRemove && (
              <>
                <Button icon="repeat" size="sm" onClick={() => setMerging(b)}>
                  {t('businessesMerge')}
                </Button>
                <Button variant="danger" icon="trash" size="sm" onClick={() => setDeleting(b)}>
                  {t('commonDelete')}
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>
      {canAdd || offersPro ? (
        <div className="row">
          <Button icon="plus" onClick={() => navigate(canAdd ? '/setup?add=1' : '/pro')}>
            {t('businessesAdd')}
          </Button>
          {!canAdd && <ProBadge />}
        </div>
      ) : (
        <p className="caption">{t('businessesLimitReached', { count: FREE_BUSINESS_LIMIT })}</p>
      )}
      {merging && user && every && (
        <MergeDialog
          from={merging}
          others={businesses.filter((b) => b.id !== merging.id)}
          onMerge={(into) => {
            const wasInUse = merging.id === business?.id;
            mergeBusiness(user.uid, every, merging.id, into.id);
            if (wasInUse) setActiveBusiness(into.id);
            toast(t('businessesMerged', { name: nameOf(into) }));
          }}
          onClose={() => setMerging(null)}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        danger
        title={deleting ? t('businessesDeleteQuestion', { name: nameOf(deleting) }) : ''}
        message={deleting ? t('businessesDeleteMessage', { name: nameOf(deleting) }) : ''}
        confirmLabel={t('commonDelete')}
        onConfirm={() => {
          if (!deleting || !user || !every) return;
          // The business in use falls back to the oldest one left.
          deleteBusiness(user.uid, every, deleting.id);
          toast(t('businessesDeleted', { name: nameOf(deleting) }));
        }}
        onClose={() => setDeleting(null)}
      />
    </SectionCard>
  );
}

function MergeDialog({ from, others, onMerge, onClose }: { from: Business; others: Business[]; onMerge: (into: Business) => void; onClose: () => void }) {
  const [intoId, setIntoId] = useState(others[0]?.id ?? '');
  const into = others.find((b) => b.id === intoId) ?? others[0];
  if (!into) return null;
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('businessesMergeQuestion', { from: nameOf(from), into: nameOf(into) })}
      actions={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('commonCancel')}
          </button>
          <button
            type="button"
            className="btn btn--action"
            onClick={() => {
              onMerge(into);
              onClose();
            }}
          >
            {t('businessesMerge')}
          </button>
        </>
      }
    >
      <div className="stack-4">
        {others.length > 1 && (
          <SelectField label={t('businessesMergeChoose', { name: nameOf(from) })} value={into.id} onChange={(e) => setIntoId(e.target.value)}>
            {others.map((b) => (
              <option key={b.id} value={b.id}>
                {nameOf(b)}
              </option>
            ))}
          </SelectField>
        )}
        <p className="body ink2">{t('businessesMergeMessage', { from: nameOf(from), into: nameOf(into) })}</p>
      </div>
    </Dialog>
  );
}

function ItemsSection() {
  const { user, items = [], proUnlocked } = useAppData();
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<SavedItem | null>(null);
  const q = query.trim().toLowerCase();
  const visible = items.filter((i) => !q || i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
  if (!proUnlocked) {
    return (
      <SectionCard title={t('itemsTitle')} detail={t('itemsNoneBody')}>
        <ButtonLink to="/pro" variant="action">
          {t('commonPro')}
        </ButtonLink>
      </SectionCard>
    );
  }
  return (
    <SectionCard title={t('itemsTitle')} detail={items.length ? undefined : t('itemsNoneBody')}>
      {items.length > 0 && (
        <div className="stack-3">
          <label className="search">
            <Icon name="search" size={18} />
            <span className="vh">{t('itemsSearchHint')}</span>
            <input type="search" value={query} placeholder={t('itemsSearchHint')} onChange={(e) => setQuery(e.target.value)} />
          </label>
          {visible.length === 0 ? (
            <p className="muted">{t('itemsNoMatch', { query: query.trim() })}</p>
          ) : (
            <ul className="saved-items">
              {visible.map((item) => (
                <li key={item.id}>
                  <button type="button" className="saved-items__row" onClick={() => setEditing(item)}>
                    <span className="saved-items__name">
                      <strong>{item.name}</strong>
                      {item.description && <span className="muted">{item.description}</span>}
                    </span>
                    <span className="amount">{new Money(item.rateMinor, CURRENCIES.find((c) => c.code === item.currency)!).format()}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {editing && user && (
        <ItemEditor
          item={editing}
          onSave={(item) => saveItem(user.uid, { ...item, updatedAt: nowStamp() })}
          onDelete={() => {
            deleteItem(user.uid, editing.id);
            track('saved_item_deleted');
            toast(t('itemsDeleted', { name: editing.name }));
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </SectionCard>
  );
}

function ItemEditor({ item, onSave, onDelete, onClose }: { item: SavedItem; onSave: (item: SavedItem) => void; onDelete: () => void; onClose: () => void }) {
  const currency = CURRENCIES.find((c) => c.code === item.currency)!;
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description);
  const [rate, setRate] = useState(new Money(item.rateMinor, currency).format({ withSymbol: false }));
  const [showErrors, setShowErrors] = useState(false);
  const parsed = Money.tryParse(rate, currency);
  const nameError = name.trim() ? null : t('editorSayWhatThisItemIs');
  const rateError = parsed ? null : t('editorEnterAPriceLike25');
  const submit = () => {
    if (nameError || rateError || !parsed) {
      setShowErrors(true);
      return;
    }
    onSave({ ...item, name: name.trim(), description: description.trim(), rateMinor: parsed.minor });
    onClose();
  };
  return (
    <Dialog
      open
      onClose={onClose}
      onSubmit={submit}
      title={t('editorEditItem')}
      actions={
        <>
          <Button
            variant="danger"
            onClick={() => {
              onDelete();
              onClose();
            }}
          >
            {t('itemsDelete')}
          </Button>
          <Button type="submit" variant="action">
            {t('itemsSave')}
          </Button>
        </>
      }
    >
      <TextField label={t('editorItem')} value={name} error={showErrors ? nameError : null} onChange={(e) => setName(e.target.value)} />
      <TextField label={t('editorDescription')} value={description} placeholder={t('editorOptionalEG10Second')} onChange={(e) => setDescription(e.target.value)} />
      <TextField label={`${t('editorRate')} (${currency.symbol})`} value={rate} inputMode="decimal" error={showErrors ? rateError : null} onChange={(e) => setRate(e.target.value)} />
    </Dialog>
  );
}

function AppearanceSection() {
  const [theme, setTheme] = useTheme();
  const [sharing, setSharing] = useState(isUsageSharingOn);
  const choices: { id: ThemeChoice; icon: IconName; label: string }[] = [
    { id: 'system', icon: 'layout', label: t('webThemeSystem') },
    { id: 'light', icon: 'sun', label: t('themeLight') },
    { id: 'dark', icon: 'moon', label: t('themeDark') },
  ];
  return (
    <SectionCard title={t('webAppearance')}>
      <fieldset className="stack-2">
        <legend className="field__label">{t('commonTheme')}</legend>
        <div className="segmented" role="group">
          {choices.map((c) => (
            <button key={c.id} type="button" aria-pressed={theme === c.id} onClick={() => setTheme(c.id)} className="row" style={{ gap: 6 }}>
              <Icon name={c.icon} size={16} />
              {c.label}
            </button>
          ))}
        </div>
      </fieldset>
      <Switch
        label={t('settingsShareUsage')}
        detail={t('webShareUsageDetail')}
        checked={sharing}
        onChange={(on) => {
          setUsageSharing(on);
          setSharing(on);
        }}
      />
    </SectionCard>
  );
}

function AccountSection() {
  const { user } = useAppData();
  const navigate = useNavigate();
  const toast = useToast();
  const [confirm, setConfirm] = useState<'signOut' | 'delete' | null>(null);
  const guest = user?.isGuest ?? true;
  return (
    <SectionCard title={t('webAccount')}>
      <div className="row">
        <span className="tile tile--lg">
          <Icon name={guest ? 'cloudup' : 'shield'} size={24} />
        </span>
        <div className="grow stack-1">
          <b className="headline">{guest ? t('settingsGuest') : t('settingsBackedUp')}</b>
          <span className="caption">{guest ? t('webGuestBannerBody') : (user?.email ?? t('settingsSignedIn'))}</span>
        </div>
        {guest ? (
          <ButtonLink to="/sign-in" variant="action" icon="cloudup">
            {t('settingsBackUpYourInvoices')}
          </ButtonLink>
        ) : (
          <Button icon="logout" onClick={() => setConfirm('signOut')}>
            {t('commonSignOut')}
          </Button>
        )}
      </div>
      <hr className="divider" />
      <div className="stack-2">
        <span className="overline">{t('settingsAbout')}</span>
        <div className="row-wrap">
          <a href="/privacy" target="_blank" rel="noreferrer">
            {t('settingsPrivacyPolicy')}
          </a>
          <span aria-hidden>·</span>
          <a href="/terms" target="_blank" rel="noreferrer">
            {t('settingsTerms')}
          </a>
          <span aria-hidden>·</span>
          <a href="/support" target="_blank" rel="noreferrer">
            {t('webHelp')}
          </a>
        </div>
        <span className="caption">{t('settingsVersion', { version: __APP_VERSION__ })}</span>
      </div>
      <hr className="divider" />
      <div className="between">
        <div className="stack-1">
          <b className="body-strong">{guest ? t('webDeleteEverything') : t('settingsDeleteAccountAndData')}</b>
          <span className="caption">{guest ? t('settingsEveryInvoiceClientAndYourBusiness') : t('settingsYourAccountEveryInvoiceClientAnd')}</span>
        </div>
        <Button variant="danger" icon="trash" onClick={() => setConfirm('delete')}>
          {t('commonDelete')}
        </Button>
      </div>
      <ConfirmDialog
        open={confirm === 'signOut'}
        title={t('settingsSignOutQuestion')}
        message={t('settingsYourInvoicesStayBackedUpSign')}
        confirmLabel={t('commonSignOut')}
        onConfirm={async () => {
          await signOut();
          track('sign_out');
          navigate('/welcome', { replace: true });
        }}
        onClose={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        danger
        title={guest ? t('webDeleteEverythingQuestion') : t('settingsDeleteYourAccount')}
        message={guest ? t('settingsEveryInvoiceClientAndYourBusiness') : t('settingsYourAccountEveryInvoiceClientAnd')}
        confirmLabel={t('commonDelete')}
        onConfirm={async () => {
          if (!navigator.onLine) {
            toast(t('settingsDeletingNeedsTheInternetConnectAnd'), { error: true });
            return;
          }
          if (!canDeleteAccountNow()) {
            toast(t('settingsForYourSecuritySignOutAnd'), { error: true });
            return;
          }
          try {
            track('account_deleted', { was_guest: guest });
            // Deletes the business, clients, invoices, saved items and public pages first.
            await deleteAccount();
            navigate('/welcome', { replace: true });
          } catch {
            toast(t('settingsDeleteFailed'), { error: true });
          }
        }}
        onClose={() => setConfirm(null)}
      />
    </SectionCard>
  );
}
